"use client"
import { usePathname } from "next/navigation"
import React, { useEffect, useMemo, useState } from "react"

// Components
import IconShoppingBag from "@/app/(site)/_components/Atoms/Icons/IconShoppingBag"
import ButtonPill from "../../../Molecules/ButtonPill"

// Contexts
import {
    default as useEcommerceStore,
    default as useGlobalStore
} from "@/app/(site)/contexts/ecommerce/store"
import {
    setAddedProduct,
    useAppDispatch,
    useAppSelector
} from "@/app/(site)/contexts/Global"
import { useQueryClient } from "@tanstack/react-query"

// Translations
import { addItemToCart } from "@/actions/selection/add-item-to-selection"
import { useLocaleFromPathname } from "@/app/(site)/_utils/_hooks/useLocaleFromPathname"
import useOneTrustOptInLoad from "@/app/(site)/_utils/_hooks/useOneTrustOptInLoad"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import logger from "@/lib/logger/logger-init"
import { addToCartDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { addToCartEvent } from "@/lib/utils/tracking/gtmEvents"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { t } from "@/translations"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"

type AddToCartProps = {
    setErrorStyles: React.Dispatch<React.SetStateAction<boolean>>
    product: CheckoutAPIProduct | RelatedProduct | ProductData
    isQuickShop?: boolean
}

export interface AddToCartFormData extends FormData {
    append(name: "item", value: string | Blob, fileName?: string): void
}

// Helper function to calculate size states
const calculateSizeStates = (availableSizesContext: any[]) => {
    const isSingleSizeAvailable =
        availableSizesContext.length === 1 && availableSizesContext[0].isAvailable
    const canAddToCart = availableSizesContext.some(
        size => size.isSelected && size.isAvailable
    )
    const isAnySizeSelected = availableSizesContext.some(size => size.isSelected)
    const isNoSizeAvailable = availableSizesContext.every(size => !size.isAvailable)
    const selectedSize =
        availableSizesContext.find(size => size.isSelected) ||
        availableSizesContext[0] ||
        {}
    const item = selectedSize?.item || ""

    return {
        isSingleSizeAvailable,
        canAddToCart,
        isAnySizeSelected,
        isNoSizeAvailable,
        selectedSize,
        item
    }
}

export default function AddToCartButton({
    setErrorStyles,
    product,
    isQuickShop = false
}: AddToCartProps) {
    // URL Lang Code
    const langCode = getLangCode(usePathname().split("/")[1]) || ""
    const availableSizesContext = useAppSelector(state => state.product.availableSizes)

    // Memoize derived state to prevent unnecessary recalculations
    const {
        isSingleSizeAvailable,
        canAddToCart,
        isAnySizeSelected,
        isNoSizeAvailable,
        selectedSize,
        item
    } = useMemo(() => calculateSizeStates(availableSizesContext), [availableSizesContext])

    const [buttonLabel, setButtonLabel] = useState(
        product.available
            ? t(langCode, "product_page.add_to_cart")
            : t(langCode, "product_page.notify_me")
    )

    const [buttonIkon, setButtonIkon] = useState(
        product.available || canAddToCart || isSingleSizeAvailable
    )

    // Contexts
    const setCartMenuOpen = useEcommerceStore(state => state.toggleCartMenu)
    const toggleNotifyModal = useGlobalStore(state => state.toggleNotifyModal)
    const queryClient = useQueryClient()
    const { functionalConsent } = useOneTrustOptInLoad()
    const closeQuickShopModal = useGlobalStore(state => state.closeQuickShopModal)
    const dispatch = useAppDispatch()
    const pathname = usePathname()
    const siteContext = useSiteContext()
    // Extract locale using the reusable hook
    const locale = useLocaleFromPathname()

    // ErrorStyle function timeout
    const handleErrorStyles = (e: any) => {
        e.preventDefault()

        setErrorStyles(true)

        setTimeout(() => {
            setErrorStyles(false)
        }, 2000)
    }

    const handleAddToCart = async (
        e: React.MouseEvent<HTMLButtonElement>,
        itemString: string
    ) => {
        e.preventDefault()
        const formData: AddToCartFormData = new FormData() as AddToCartFormData
        formData.append("item", itemString)
        // Invalidate the 'selection' query to refetch data
        if (selectedSize.isAvailable) {
            setCartMenuOpen()
            await addItemToCart(formData)
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: ["selection"] })
                    addToCartEvent(
                        product,
                        {
                            size: selectedSize.name,
                            sku: selectedSize.sku
                        },
                        siteContext,
                        "pdp"
                    )
                    if (functionalConsent) {
                        addToCartDepictEvent(product, {
                            size: selectedSize.name,
                            sku: selectedSize.sku
                        })
                    }
                    dispatch(
                        setAddedProduct({
                            name: selectedSize.name,
                            size: selectedSize.name,
                            image: product?.media?.small
                                ? (product as ProductData)?.media?.small?.[0].url
                                : (product as CheckoutAPIProduct)?.mediaObjects?.[0]
                                      ?.sources?.mini?.[0]?.url || "",
                            price: product.price as string,
                            color_name: product?.color_swatch?.color_name as string
                        })
                    )
                })
                .catch(error => {
                    // @todo throw a toast error here for users
                    logger.error(
                        `Not able to add a product to the cart from ${isQuickShop ? "quickshop" : "PDP"}`,
                        error
                    )
                })
                .finally(() => {
                    if (isQuickShop) {
                        closeQuickShopModal()
                    }
                })
            return
        }
        if (!canAddToCart) {
            // No size selected
            setErrorStyles(true)

            setTimeout(() => {
                setErrorStyles(false)
            }, 1000)
            toggleNotifyModal(true)
            if (isQuickShop) {
                closeQuickShopModal()
            }
            return
        }
        if (canAddToCart) {
            setCartMenuOpen()
            await addItemToCart(formData)
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: ["selection"] })
                    addToCartEvent(
                        product,
                        {
                            size: selectedSize.name,
                            sku: selectedSize.sku
                        },
                        siteContext,
                        "pdp"
                    )
                    if (functionalConsent) {
                        addToCartDepictEvent(product, {
                            size: selectedSize.name,
                            sku: selectedSize.sku
                        })
                    }
                    dispatch(
                        setAddedProduct({
                            name: selectedSize.name,
                            size: selectedSize.name,
                            image: product?.media?.small
                                ? (product as ProductData)?.media?.small?.[0].url
                                : (product as CheckoutAPIProduct)?.mediaObjects?.[0]
                                      ?.sources?.mini?.[0]?.url || "",
                            price: product.price || "",
                            color_name: product?.color_swatch?.color_name || ""
                        })
                    )
                })
                .catch(error => {
                    logger.error(
                        `Not able to add a product to the cart from ${isQuickShop ? "quickshop" : "PDP"}`,
                        error
                    )
                })
                .finally(() => {
                    if (isQuickShop) {
                        closeQuickShopModal()
                    }
                })

            return
        }
    }

    useEffect(() => {
        const buttonStates = [
            {
                condition: isNoSizeAvailable && !isAnySizeSelected,
                label: isQuickShop
                    ? t(langCode, "product_page.out_of_stock")
                    : t(langCode, "product_page.notify_me")
            },
            {
                condition: !canAddToCart && !isAnySizeSelected,
                label: t(langCode, "product_page.select_size")
            },
            {
                condition: !canAddToCart,
                label: isQuickShop
                    ? t(langCode, "product_page.out_of_stock")
                    : t(langCode, "product_page.notify_me")
            },
            {
                condition: canAddToCart || isSingleSizeAvailable,
                label: t(langCode, "product_page.add_to_cart")
            }
        ]

        const matchingState = buttonStates.find(state => state.condition)
        const newLabel = matchingState?.label || t(langCode, "product_page.add_to_cart")

        if (newLabel !== buttonLabel) {
            setButtonLabel(newLabel)
        }
    }, [
        isSingleSizeAvailable,
        isNoSizeAvailable,
        isAnySizeSelected,
        canAddToCart,
        isQuickShop,
        langCode,
        buttonLabel
    ])

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()

        if (canAddToCart || isSingleSizeAvailable) {
            handleAddToCart(e, item)
        } else if (
            (!canAddToCart && !isQuickShop) ||
            (isNoSizeAvailable && !isAnySizeSelected && !isQuickShop)
        ) {
            toggleNotifyModal(true)
        } else {
            handleErrorStyles(e)
        }
    }

    return (
        <ButtonPill
            color="black"
            variant="primary"
            size="large"
            onClick={handleButtonClick}
            className="w-full md:w-fit"
            aria-label={buttonLabel}
            type="submit"
            disabled={buttonLabel === t(langCode, "product_page.out_of_stock")}
        >
            {buttonLabel}
            {buttonIkon && (
                <IconShoppingBag aria-hidden="true" className="ml-8 h-16 w-16" />
            )}
        </ButtonPill>
    )
}
